import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Menu, X } from "lucide-react";
import "./PublicHome.css";

const HERO_PHRASES = Object.freeze([
  "le bruit des vagues ?",           // Vagues
  "le chant des oiseaux ?",          // Oiseaux
  "la pluie sur une verrière ?",     // Pluie
  "le feu qui crépite ?",            // Feu crépitant
  "le murmure d'un ruisseau ?",     // Ruisseau
  "l'orage qui gronde au loin ?",    // Tonnerre
  "la résonance des bols tibétains ?", // Bols tibétains
  "les mouettes au-dessus de l'océan ?", // Mouettes et Océan
  "le vent glacé de la toundra ?",   // Toundra arctique
  "le chant des baleines ?",         // Chant des baleines
  "le ronronnement d'un chat ?",     // Ronronnement
  "un souffle blanc et enveloppant ?", // Bruit Blanc
]);

const HERO_HOLD_DURATION = 5500;
const HERO_FADE_DURATION = 1200;

const HERO_IMAGES = Object.freeze([
  { mobile: "/publichome/1.jpg", desktop: "/publichome/desktop/1.jpg" },
  { mobile: "/publichome/2.jpg", desktop: "/publichome/desktop/2.jpg" },
  { mobile: "/publichome/3.jpg", desktop: "/publichome/desktop/3.jpg" },
  { mobile: "/publichome/4.jpg", desktop: "/publichome/desktop/4.jpg" },
  { mobile: "/publichome/5.jpg", desktop: "/publichome/desktop/5.jpg" },
  { mobile: "/publichome/6.jpg", desktop: "/publichome/desktop/6.jpg" },
  { mobile: "/publichome/7.jpg", desktop: "/publichome/desktop/7.jpg" },
  { mobile: "/publichome/8.jpg", desktop: "/publichome/desktop/8.jpg" },
  { mobile: "/publichome/9.jpg", desktop: "/publichome/desktop/9.jpg" },
  { mobile: "/publichome/10.jpg", desktop: "/publichome/desktop/10.jpg" },
  { mobile: "/publichome/11.jpg", desktop: "/publichome/desktop/11.jpg" },
  { mobile: "/publichome/12.jpg", desktop: "/publichome/desktop/12.jpg" },
]);

const NAV_LINKS = Object.freeze([
  { label: "Comment ça marche", href: "#experience" },
  { label: "Le Refuge Personnel", href: "#ia" },
  { label: "Tarifs", href: "#prix" },
  { label: "Questions", href: "#faq" },
  { label: "Blog", href: "/blog" },
]);

function HeroImageLayer({ mobileSrc, desktopSrc, isActive }) {
  return (
<picture
  className={`public-home-hero__image ${isActive ? "is-active" : ""}`}
>
      <source
        media="(min-width: 768px)"
        srcSet={desktopSrc}
      />
      <img
        src={mobileSrc}
        alt=""
        loading={isActive ? "eager" : "lazy"}
        decoding="async"
      />
    </picture>
  );
}

const PublicHome = ({
  onConnect,
  onSignup,
  onStartDemo,
  onSubscribe,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [heroPhraseIndex, setHeroPhraseIndex] = useState(0);
  const [heroPhraseVisible, setHeroPhraseVisible] = useState(true);
  const [showContactChooser, setShowContactChooser] = useState(false);
  const [bridgeVisible, setBridgeVisible] =
  useState(false);

  const heroCtaRef = useRef(null);
  const finalCtaRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const bridgeRef = useRef(null);
  
useLayoutEffect(() => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    /*
      Sécurité pour certains navigateurs mobiles
      et certaines structures CSS.
    */
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const supportsScrollRestoration =
    "scrollRestoration" in window.history;

  const previousScrollRestoration =
    supportsScrollRestoration
      ? window.history.scrollRestoration
      : null;

  if (supportsScrollRestoration) {
    window.history.scrollRestoration = "manual";
  }

  /*
    Une première fois avant le rendu,
    puis une seconde après la mise en page.
  */
  scrollToTop();

  const frameId =
    window.requestAnimationFrame(scrollToTop);

  return () => {
    window.cancelAnimationFrame(frameId);

    if (
      supportsScrollRestoration &&
      previousScrollRestoration
    ) {
      window.history.scrollRestoration =
        previousScrollRestoration;
    }
  };
}, []);

useEffect(() => {
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  if (reduceMotion) return undefined;

  let holdTimer;
  let fadeTimer;

  const scheduleNextHero = () => {
    holdTimer = window.setTimeout(() => {
      setHeroPhraseVisible(false);

      fadeTimer = window.setTimeout(() => {
        setHeroPhraseIndex((current) => (current + 1) % HERO_PHRASES.length);
        setHeroPhraseVisible(true);
        scheduleNextHero();
      }, HERO_FADE_DURATION);
    }, HERO_HOLD_DURATION);
  };

  scheduleNextHero();

  return () => {
    window.clearTimeout(holdTimer);
    window.clearTimeout(fadeTimer);
  };
}, []);

  useEffect(() => {
  const element = bridgeRef.current;

  if (!element) return undefined;

  const reduceMotion =
    window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;

  if (
    reduceMotion ||
    !("IntersectionObserver" in window)
  ) {
    setBridgeVisible(true);
    return undefined;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;

      setBridgeVisible(true);
      observer.disconnect();
    },
    {
      threshold: 0.32,
      rootMargin: "0px 0px -8% 0px",
    },
  );

  observer.observe(element);

  return () => {
    observer.disconnect();
  };
}, []);

const handleStartFree = () => {
  if (onStartDemo) onStartDemo({ intent: "free" });
  else if (onSignup) onSignup();
  else window.location.href = "/auth/signup";
};

const handleUpgradePremium = () => {
  if (onSubscribe) onSubscribe(billing);
  else if (onSignup) onSignup();
  else window.location.href = "/auth/signup";
};

  const handleLogin = () => {
    if (onConnect) onConnect();
    else window.location.href = "/auth/login";
  };

const closeMobile = () => setMobileOpen(false);

const handleNavClick = (event, href) => {
  if (!href.startsWith("#")) { closeMobile(); return; }

  const target = document.querySelector(href);
  if (!target) return;

  event.preventDefault();
  event.stopPropagation();
  closeMobile();

  /* Le verrou de scroll (body.nav-open) est retiré au rendu suivant :
     on attend cette frame avant de lancer le défilement. */
  window.requestAnimationFrame(() => target.scrollIntoView({ behavior: "smooth", block: "start" }));
};

const faqs = useMemo(
  () => [
    {
      q: "Harmonauria, qu’est-ce que c’est ?",
      a: "Harmonauria est un Refuge numérique pour les moments où le monde devient trop bruyant. Vous y retrouvez des respirations guidées, des musiques composées à la main et des paysages sonores conçus pour vous aider à ralentir et à vous recentrer.",
    },
    {
      q: "Est-ce une application de méditation ?",
      a: "Non. Vous n’avez pas besoin de méditer, de suivre un cours ou de maîtriser une technique. Harmonauria vous accompagne simplement pendant quelques minutes avec une respiration, une musique et une ambiance sonore.",
    },
    {
      q: "Pourquoi ne pas aller directement sur YouTube ou Spotify ?",
      a: "Vous pouvez bien sûr y écouter des sons relaxants. Harmonauria propose une expérience différente : vous n’avez pas à chercher parmi des centaines de contenus, la respiration et la musique sont pensées ensemble, les paysages sonores peuvent être ajustés et chaque session devient un véritable rituel sans interruption ni distraction.",
    },
    {
      q: "La musique est-elle générée par une intelligence artificielle ?",
      a: "Non. Chaque morceau est composé à la main. Le Refuge Personnel s’enrichit régulièrement de nouvelles créations originales. L’intelligence artificielle intervient uniquement en arrière-plan pour personnaliser l’accompagnement, jamais pour remplacer la création musicale.",
    },
    {
      q: "À quoi sert le Refuge Personnel ?",
      a: "Le Refuge Personnel retient progressivement les ambiances que vous aimez, les moments où vous utilisez Harmonauria et les expériences qui vous font le plus de bien. Il peut ainsi vous proposer des sessions plus proches de votre rythme, sans vous demander de tout choisir à chaque fois.",
    },
    {
      q: "Je n’ai jamais fait de respiration guidée. Est-ce pour moi ?",
      a: "Oui. Vous n’avez rien à connaître avant de commencer. Les indications vous accompagnent doucement, respiration après respiration. Vous pouvez également arrêter ou recommencer à tout moment.",
    },
    {
      q: "Ai-je besoin d’un casque ?",
      a: "Non. Harmonauria peut être utilisé avec les haut-parleurs de votre téléphone ou de votre ordinateur. Un casque offre simplement une immersion plus profonde et permet de mieux percevoir certains effets stéréo.",
    },
    {
      q: "Harmonauria peut-elle remplacer un suivi médical ?",
      a: "Non. Harmonauria est un espace de détente et d’accompagnement. Il ne remplace pas l’avis d’un médecin ou d’un professionnel de santé. En cas d’anxiété importante, persistante ou invalidante, il est recommandé de demander un accompagnement adapté.",
    },
{
  q: "Comment fonctionne l’essai de 7 jours ?",
  a: "Vous accédez pendant 7 jours au Refuge Personnel et à toutes les expériences qu’il inclut. L’abonnement démarre ensuite selon la formule choisie, sauf annulation depuis votre compte.",
},
  ],
  [],
);

  // Scroll lock quand menu mobile ouvert
  useEffect(() => {
    document.body.classList.toggle("nav-open", mobileOpen);
    return () => document.body.classList.remove("nav-open");
  }, [mobileOpen]);

  // Le hamburger disparaît au-delà de 1023px : on referme pour libérer le scroll.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const query = window.matchMedia("(min-width: 1024px)");
    if (query.matches) { closeMobile(); return undefined; }
    const onChange = (event) => { if (event.matches) closeMobile(); };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [mobileOpen]);

  // Fermer menu au clavier
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMobile();
    };
    if (mobileOpen) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);
  useEffect(() => {
    if (!mobileOpen) return;

    const handlePointerDown = (e) => {
      const target = e.target;

      const clickedInsideMenu = mobileMenuRef.current?.contains(target);
      const clickedHamburger = hamburgerRef.current?.contains(target);

      if (!clickedInsideMenu && !clickedHamburger) {
        closeMobile();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [mobileOpen]);

  // Smooth scroll ancres
  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || href.length <= 1) return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        closeMobile();
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Animation apparition sections (respecte prefers-reduced-motion)
  useEffect(() => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reduceMotion) return;

    const sections = document.querySelectorAll("section:not(.hero)");
    sections.forEach((s) => {
      s.style.opacity = "0";
      s.style.transform = "translateY(20px)";
      s.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";
    });

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );

    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  // Sticky bar visible seulement quand les CTAs principaux ne sont pas visibles
  useEffect(() => {
    const heroEl = heroCtaRef.current;
    const finalEl = finalCtaRef.current;
    if (!heroEl || !finalEl) return;

    let heroVisible = true;
    let finalVisible = false;

    const compute = () => setShowStickyBar(!heroVisible && !finalVisible);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === heroEl) heroVisible = entry.isIntersecting;
          if (entry.target === finalEl) finalVisible = entry.isIntersecting;
        });
        compute();
      },
      { threshold: 0.15 },
    );

    io.observe(heroEl);
    io.observe(finalEl);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!showContactChooser) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowContactChooser(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showContactChooser]);

  const toggleFaq = (i) => setOpenFaqIndex((prev) => (prev === i ? null : i));

  const contactTo = "harmonauria.music@gmail.com";
  const contactSubject = "Contact — Harmonauria";
  const contactBody = `Bonjour,

Je vous contacte au sujet de :

(écrivez votre message ici)

Merci,
`;

  const contactMailtoHref = `mailto:${contactTo}?subject=${encodeURIComponent(contactSubject)}&body=${encodeURIComponent(contactBody)}`;

  const contactGmailHref = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(contactTo)}&su=${encodeURIComponent(contactSubject)}&body=${encodeURIComponent(contactBody)}`;

  const contactOutlookHref = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(contactTo)}&subject=${encodeURIComponent(contactSubject)}&body=${encodeURIComponent(contactBody)}`;

  const contactYahooHref = `https://compose.mail.yahoo.com/?to=${encodeURIComponent(contactTo)}&subject=${encodeURIComponent(contactSubject)}&body=${encodeURIComponent(contactBody)}`;

  return (
    <>
      <Helmet>
<title>
  Harmonauria — Votre Refuge pour respirer et retrouver le calme
</title>

<meta
  name="description"
  content="Respirations guidées, musiques composées à la main et paysages sonores pour ralentir, souffler et retrouver un peu de calme."
/>

<meta
  property="og:title"
  content="Harmonauria — Prenez une inspiration"
/>

<meta
  property="og:description"
  content="Un Refuge numérique pour respirer, ralentir et laisser le reste attendre quelques minutes."
/>

<meta
  name="twitter:title"
  content="Harmonauria — Prenez une inspiration"
/>

<meta
  name="twitter:description"
  content="Respirations guidées et paysages sonores pour retrouver calme et détente."
/>
        <meta
          name="twitter:image"
          content="https://harmonauria.fr/og-cover.jpg"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
              },
            })),
          })}
        </script>
      </Helmet>
      <div className="public-home-embedded">
{createPortal(
  <nav
    className={`public-home-nav ${mobileOpen ? "is-open" : ""}`}
    aria-label="Sections de la page"
  >
    <button
      type="button"
      ref={hamburgerRef}
      className="menu-button public-home-nav__toggle"
      aria-expanded={mobileOpen}
      aria-controls="public-home-nav-links"
      aria-haspopup="true"
      aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
      onClick={() => setMobileOpen((open) => !open)}
    >
      {mobileOpen ? <X size={22} /> : <Menu size={22} />}
    </button>

    <div
      id="public-home-nav-links"
      ref={mobileMenuRef}
      className="public-home-nav__links"
    >
      {NAV_LINKS.map((link) => (
        <a
          key={link.href}
          href={link.href}
          className="public-home-nav__link"
          onClick={(event) => handleNavClick(event, link.href)}
        >
          {link.label}
        </a>
      ))}
    </div>
  </nav>,
  document.body,
)}
        {createPortal(
          <div className="public-home-auth-actions" role="navigation" aria-label="Accès au compte">
            <button type="button" className="public-home-auth-actions__login" onClick={handleLogin}>Se connecter</button>
            <button type="button" className="public-home-auth-actions__signup" onClick={() => { if (onSignup) onSignup(); else window.location.href = "/auth/signup"; }}>S'inscrire</button>
          </div>,
          document.body,
        )}

        <main id="top">
          {/* HERO — accueil Harmonauria */}
          <section className="hero public-home-hero" aria-labelledby="public-home-hero-title">

<div className="public-home-hero__images" aria-hidden="true">
{HERO_IMAGES.map(({ mobile, desktop }, index) => (
  <HeroImageLayer
    key={mobile}
    mobileSrc={mobile}
    desktopSrc={desktop}
    isActive={index === heroPhraseIndex % HERO_IMAGES.length}
  />
))}
</div>
            <div className="public-home-hero__veil" aria-hidden="true" />

            <div className="public-home-hero__content">
              <h1 id="public-home-hero-title" className="public-home-hero__title">
                Et si votre meilleure notification était...
              </h1>

              <p
                className={`public-home-hero__phrase ${
                  heroPhraseVisible ? "is-visible" : ""
                }`}
                aria-live="polite"
                aria-atomic="true"
              >
                {HERO_PHRASES[heroPhraseIndex]}
              </p>

              <p className="public-home-hero__subtitle">
                Respirations guidées et paysages sonores pour retrouver calme et
                détente.
              </p>

              <div className="public-home-hero__cta" ref={heroCtaRef}>
                <button
                  type="button"
                  className="btn btn-primary public-home-hero__button"
                  onClick={handleStartFree}
                >
                  Commencer à respirer
                </button>
              </div>
            </div>
          </section>

{/* TRANSITION — DU BRUIT VERS LE REFUGE */}
<div
  ref={bridgeRef}
  className={`public-home-bridge ${
    bridgeVisible ? "is-visible" : ""
  }`}
  aria-label="Transition vers l’expérience Harmonauria"
>
  <div className="public-home-bridge__content">
    <span className="public-home-bridge__kicker">
      Ça vous arrive ?
    </span>

    <p className="public-home-bridge__problem">
      Les journées s’enchaînent.
      <br />
      Les pensées aussi.
    </p>

    <div
      className="public-home-bridge__shift"
      aria-hidden="true"
    >
      <span />
    </div>

    <p className="public-home-bridge__answer">
      <strong>Retrouvez votre calme.</strong>{" "}
      Offrez-vous quelques minutes pour souffler,
      relâcher la pression et revenir à vous.
    </p>
  </div>
</div>

{/* LE REFUGE HARMONAURIA */}
<section id="experience" className="problem-solution">
  <div className="problem-solution__header">
    <span className="problem-solution__eyebrow">
      L’expérience Harmonauria
    </span>

    <h2 className="section-heading">
      Trois gestes simples pour retrouver votre calme
    </h2>

    <p className="section-intro">
      Tout est pensé pour vous aider à ralentir, sans avoir à apprendre,
      réussir ou comprendre quoi que ce soit.
    </p>
  </div>

  <div className="problem-solution-inner">
    <div
      className="solution-pillars"
      aria-label="Trois gestes pour retrouver le calme"
    >
      {[
        {
          icon: "🫁",
          title: "Respirer",
          text: "Une respiration guidée vous accompagne doucement. Vous n’avez pas à compter ni à réfléchir : il suffit de vous laisser guider.",
        },
        {
          icon: "🌿",
          title: "S’immerger",
          text: "Pluie sur une verrière, vagues lentes, feu qui crépite ou vent dans les arbres : choisissez le paysage dans lequel vous avez envie de vous réfugier.",
        },
        {
          icon: "🎼",
          title: "Écouter",
          text: "Chaque musique est composée à la main avec intention, pour accompagner votre respiration sans jamais prendre toute la place.",
        },
      ].map((pillar) => (
        <article key={pillar.title} className="pillar-card">
          <div className="pillar-icon" aria-hidden="true">
            {pillar.icon}
          </div>

          <h3>{pillar.title}</h3>
          <p>{pillar.text}</p>
        </article>
      ))}
    </div>

    <div className="science-explanation refuge-explanation">
<div className="refuge-explanation__icon" aria-hidden="true">
  <img
    className="refuge-explanation__logo"
    src="/refuge-symbol.svg"
    alt=""
    width={30}
    height={30}
    decoding="async"
  />
</div>

      <div className="refuge-explanation__content">
        <h3>Pourquoi cela aide ?</h3>

        <p>
          Lorsque le souffle, la musique et le paysage sonore avancent
          ensemble, il devient plus facile de quitter le rythme de la
          journée et de revenir doucement à soi.
        </p>

        <p className="science-note">
          Vous n’avez rien à réussir. Seulement quelques minutes à vous
          accorder.
        </p>
      </div>
    </div>
  </div>
</section>

{/* RESSENTI */}
<section id="resultats" className="benefits-section">
<h2 className="section-heading">
  Quand le calme retrouve sa place
</h2>

<p className="section-intro">
  Le monde continue autour de vous. Pendant quelques minutes,
  votre corps peut relâcher, votre esprit s’alléger et le moment
  redevenir le vôtre.
</p>

  <div className="benefits-grid" aria-label="Ressentis possibles">
    {[
  {
    title: "🌿 Le corps relâche",
    subtitle: "Desserrer les tensions",
    text: "Le souffle devient plus lent. Les épaules descendent. Votre corps comprend progressivement qu’il peut enfin se poser.",
  },
  {
    title: "☁️ L’esprit s’allège",
    subtitle: "Retrouver de l’espace",
    text: "Les pensées sont toujours là, mais elles cessent peu à peu d’occuper toute la place.",
  },
  {
    title: "🧘 Un moment à vous",
    subtitle: "Revenir à soi",
    text: "Quelques minutes sans notification, sans attente et sans rien à réussir. Seulement un endroit où vous pouvez être présent.",
  },
    ].map((benefit) => (
      <div key={benefit.title} className="benefit-card">
        <div className="benefit-header">
          <h3>{benefit.title}</h3>

          <span className="benefit-subtitle">
            {benefit.subtitle}
          </span>
        </div>

        <div className="benefit-divider" />

        <p className="benefit-text">{benefit.text}</p>
      </div>
    ))}
  </div>

  <div className="science-explanation">
    <h3>Le calme ne se force pas.</h3>

    <p>
      Il se retrouve dans des gestes simples que l’on prend plaisir
      à répéter : une respiration, un paysage sonore, quelques minutes
      sans interruption.
    </p>

    <p className="science-note">
      Revenez lorsque vous en ressentez le besoin.
      Harmonauria n’est pas une performance. C’est votre Refuge.
    </p>
  </div>
</section>

{/* REFUGE PERSONNEL */}
<section id="ia" className="ai-showcase">
  <div className="ai-showcase-inner">
    <div className="ai-showcase-kicker">
      Refuge Personnel
    </div>
<h2 className="section-heading">
  Un Refuge qui apprend à vous connaître
</h2>

    <p className="ai-showcase-lead">
      Vous n’avez pas à chercher la bonne respiration, la bonne
      musique ou la bonne ambiance. Dites simplement comment vous
      vous sentez, et Harmonauria prépare un moment adapté à votre
      besoin.
    </p>

    <div className="ai-steps-grid">
      <div className="ai-step-card">
        <div className="ai-step-num">1</div>

        <h3>Dites ce dont vous avez besoin</h3>

        <p>
          Une journée trop lourde, un esprit agité, une tension qui ne
          redescend pas ou simplement l’envie de vous accorder une pause.
        </p>
      </div>

      <div className="ai-step-card">
        <div className="ai-step-num">2</div>

        <h3>Laissez votre session prendre forme</h3>

        <p>
          Harmonauria associe une respiration, une musique composée à
          la main et un paysage sonore pour créer un moment cohérent
          et apaisant.
        </p>
      </div>

      <div className="ai-step-card">
        <div className="ai-step-num">3</div>

        <h3>Retrouvez ce qui vous fait du bien</h3>

        <p>
          Au fil de vos ressentis, votre Refuge retient vos préférences
          et vous propose des expériences plus proches de votre rythme.
        </p>
      </div>
    </div>

    <p
      style={{
        maxWidth: 680,
        margin: "0 auto 2rem",
        textAlign: "center",
      }}
    >
Votre Refuge s’enrichit de nouvelles musiques composées à la main chaque mois. Écoutez-les seules, ou laissez Harmonauria y associer une respiration guidée et votre paysage sonore.
    </p>

    <div className="ai-showcase-cta">
      <button
        type="button"
        className="btn btn-premium"
        onClick={handleUpgradePremium}
      >
        Créer mon Refuge Personnel
      </button>

<div className="cta-undertext">
  7 jours offerts · Puis à partir de 4,99 €/mois · Sans engagement
</div>
    </div>
  </div>
</section>


          {/* PRICING */}
          <section id="prix" className="pricing-section">
<h2 className="section-heading">
  Commencez gratuitement. Créez votre Refuge Personnel quand vous le souhaitez.
</h2>

            {/* Toggle Mensuel / Annuel */}
            <div
              className="pricing-billing-toggle pricing-billing-toggle--desktop"
              role="tablist"
              aria-label="Choisir la facturation"
            >
              <button
                type="button"
                role="tab"
                aria-selected={billing === "monthly"}
                className={`billing-toggle-btn ${billing === "monthly" ? "active" : ""}`}
                onClick={() => setBilling("monthly")}
              >
                Mensuel
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={billing === "yearly"}
                className={`billing-toggle-btn ${billing === "yearly" ? "active" : ""}`}
                onClick={() => setBilling("yearly")}
              >
                Annuel
                <span className="billing-toggle-chip">-28%</span>
              </button>
            </div>

            <div className="pricing-grid">
              {/* FREE */}
              <div className="pricing-card pricing-free">
                <h3 className="pricing-title">Découverte</h3>

<div className="pricing-free-note">
  Un premier pas pour découvrir la respiration guidée et vous
  accorder quelques minutes de calme.
</div>

                <ul className="pricing-features">
                  <li>Plusieurs sessions jusqu'à 2 minutes</li>
                  <li>Respiration guidée simple et immédiate</li>
                  <li>Ambiance essentielle incluse</li>
                  <li>1 guide offert pour bien débuter</li>
                </ul>

                <button
                  type="button"
                  className="btn btn-primary pricing-free-btn"
                  onClick={handleStartFree}
                >
                  Commencer gratuitement
                </button>

                <div className="pricing-free-trust">
                  <span>⚡ Démarrage immédiat</span>
                  <span>🫁 Facile à suivre</span>
                  <span>🎧 Idéal pour tester</span>
                </div>
              </div>
              {/* Toggle mobile : entre les 2 cartes pour voir le prix changer */}
              <div
                className="pricing-billing-toggle pricing-billing-toggle--mobile"
                role="tablist"
                aria-label="Choisir la facturation"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={billing === "monthly"}
                  className={`billing-toggle-btn ${billing === "monthly" ? "active" : ""}`}
                  onClick={() => setBilling("monthly")}
                >
                  Mensuel
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={billing === "yearly"}
                  className={`billing-toggle-btn ${billing === "yearly" ? "active" : ""}`}
                  onClick={() => setBilling("yearly")}
                >
                  Annuel
                  <span className="billing-toggle-chip">-28%</span>
                </button>
              </div>
              {/* PREMIUM */}
              <div className="pricing-card pricing-premium">


<h3 className="pricing-title">Refuge Personnel</h3>
<p className="pricing-subtitle">
  Un univers sonore vivant, qui s’adapte à vous
</p>

                {billing === "monthly" ? (
                  <div className="pricing-price">
                    <span className="price-main">6,99€</span>
                    <span className="price-period">/mois</span>
                  </div>
                ) : (
                  <div className="pricing-price">
                    <span className="price-main">4,99€</span>
                    <span className="price-period">/mois</span>
                  </div>
                )}

                <div className="pricing-note">
                  {billing === "yearly"
                    ? "Facturé 59,88€ TTC par an. Sans engagement, résiliable à tout moment."
                    : "TTC. Sans engagement. Résiliable à tout moment."}
                </div>

<ul className="pricing-features">
  <li className="pricing-feature-hero">
    <strong>🎼 Des créations inédites, chaque mois</strong>
    <span>Des musiques composées à la main, à écouter seules ou avec une respiration guidée.</span>
  </li>
  <li>Votre accompagnement personnel avec Auria</li>
  <li>Les 9 respirations et toutes les durées</li>
  <li>Paysages sonores exclusifs</li>
  <li>Tous les guides 21 jours</li>
  <li>Tous les fonds d'écran</li>
</ul>

                <button
                  type="button"
                  className="btn btn-premium pricing-premium-btn"
                  onClick={handleUpgradePremium}
                >
                  Créer mon Refuge Personnel
                </button>
                <p
                  className="pricing-note"
                  style={{ textAlign: "center", marginTop: "0.6rem" }}
                >
                  {billing === "yearly"
                    ? "7 jours gratuits, puis 59,88€/an. Annulable en 1 clic."
                    : "7 jours gratuits, puis 6,99€/mois. Annulable en 1 clic."}
                </p>
                <div className="pricing-trust">
                  <span>🔒 Paiement sécurisé</span>
                  <span>🔄 Sans engagement</span>
                  <span>⚡ Support rapide</span>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="objections-section section-narrow">
<h2 className="section-heading">
  Questions fréquentes
</h2>

            <div className="faq-list">
              {faqs.map((faq, i) => {
                const isOpen = openFaqIndex === i;
                const panelId = `faq-panel-${i}`;
                const buttonId = `faq-button-${i}`;
                return (
                  <div key={i} className={`faq-item ${isOpen ? "active" : ""}`}>
                    <button
                      id={buttonId}
                      type="button"
                      className="faq-question"
                      onClick={() => toggleFaq(i)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                    >
                      <span>{faq.q}</span>
                      <span className="faq-toggle" aria-hidden="true">
                        +
                      </span>
                    </button>

                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className="faq-answer"
                    >
                      <p>{faq.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="faq-cta">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartFree}
              >
                Je teste maintenant (gratuit)
              </button>
            </div>
          </section>

{/* FINAL CTA */}
<section className="final-cta">
  <div ref={finalCtaRef} />

<h2 className="section-heading">
  Le monde peut attendre quelques minutes.
</h2>

  <p
    style={{
      maxWidth: 620,
      margin: "1.5rem auto 2.5rem",
      color: "rgba(255, 255, 255, 0.86)",
      textAlign: "center",
    }}
  >
    Accordez-vous un endroit où ralentir, respirer et revenir
    doucement à vous.
  </p>

  <button
    type="button"
    className="btn btn-primary final-cta-btn"
    onClick={handleStartFree}
  >
    Commencer à respirer
  </button>

  <div className="final-reassurance">
    <span>Sans carte bancaire</span>
    <span>Sans engagement</span>
    <span>À votre rythme</span>
  </div>

  <p className="final-disclaimer">
    Harmonauria accompagne vos moments de détente et ne remplace pas
    un avis médical.
  </p>
</section>
        </main>

        {showStickyBar && (
          <div
            className="sticky-bar visible"
            role="region"
            aria-label="Raccourci d’action"
          >
<span className="sticky-bar-text">
  Besoin de souffler ?
</span>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleStartFree}
            >
              Respirer
            </button>
          </div>
        )}

        <footer>
          <div className="footer-columns">
            <nav className="footer-column" aria-labelledby="footer-harmonauria-title">
              <p id="footer-harmonauria-title" className="footer-column__title">
                Harmonauria
              </p>
              <Link to="/science">Sources scientifiques</Link>
              <Link to="/apropos">À propos</Link>
              <Link to="/frequences">Sons &amp; fréquences</Link>
              <Link to="/blog">Blog</Link>
              <button
                type="button"
                className="footer-link-btn"
                onClick={() => setShowContactChooser(true)}
              >
                Contact
              </button>
            </nav>

            <nav className="footer-column" aria-labelledby="footer-legal-title">
              <p id="footer-legal-title" className="footer-column__title">
                Informations légales
              </p>
              <Link to="/legal/mentions-legales">Mentions légales</Link>
              <Link to="/legal/cgv">CGV</Link>
              <Link to="/legal/politique-confidentialite">Confidentialité</Link>
              <Link to="/legal/remboursement">Remboursement</Link>
            </nav>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; {new Date().getFullYear()} Harmonauria. Tous droits
              réservés.
            </p>
            <p>Prenez une inspiration. Le reste peut attendre.</p>
          </div>
        </footer>
      </div>
      {showContactChooser && createPortal(
        <div
          className="email-modal-overlay"
          role="presentation"
          onClick={() => setShowContactChooser(false)}
        >
          <div
            className="email-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Choisir votre boîte mail"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="email-modal-title">
              Où souhaitez-vous rédiger votre message ?
            </h3>

            <div className="email-modal-actions">
              <a
                className="email-modal-btn"
                href={contactGmailHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => setShowContactChooser(false)}
              >
                Gmail
              </a>

              <a
                className="email-modal-btn"
                href={contactOutlookHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => setShowContactChooser(false)}
              >
                Outlook
              </a>

              <a
                className="email-modal-btn"
                href={contactYahooHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => setShowContactChooser(false)}
              >
                Yahoo
              </a>

              <a
                className="email-modal-btn secondary"
                href={contactMailtoHref}
                onClick={() => setShowContactChooser(false)}
              >
                Application email (par défaut)
              </a>
            </div>

            <button
              type="button"
              className="email-modal-cancel"
              onClick={() => setShowContactChooser(false)}
            >
              Annuler
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default PublicHome;
